// ============================================================
// CarePeopleRolePanel.jsx  (2b-13 Gate 5)
// User-detail panel for managing a user's CarePeople MODULE role
// (HR / Supervisor + elevation tier). Rendered inside the Agent
// Access tab of <UserDetailPage>.
//
// Self-gating: fetches GET .../carepeople-role, which returns
// `coreRoleApplicable`. Module roles apply only to admin/auditor
// core-role users; for manager/carer the panel shows a short
// "not applicable" note (they have role-defined CarePeople access).
//
// Sections:
//   - Current role (None | HR/Supervisor + tier + assigned by/at)
//   - [Change role] (assign modal) / [Revoke access] (confirm)
//   - Role history (grant/revoke audit, newest first)
//
// Backend is the source of truth — no optimistic UI. Every mutation
// awaits the API then re-fetches the panel.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, RotateCw, ShieldCheck } from 'lucide-react';
import Badge from './Badge';
import ConfirmDialog from './ConfirmDialog';
import CarePeopleRoleAssignModal from './CarePeopleRoleAssignModal';
import { toast } from './Toaster';
import { getCarePeopleRole, grantCarePeopleRole, revokeCarePeopleRole } from '../api/carePeopleRoles';
import { translateError } from '../utils/errorMessages';
import StatusMessage from './StatusMessage';
import { formatDate } from '../utils/format';
import './CarePeopleRolePanel.css';

const ROLE_LABEL = { hr: 'HR', supervisor: 'Supervisor', hr_manager: 'HR Manager' };

// Per-role core-role eligibility (role-model expansion §6.3, 2026-07-13),
// mirrored from the grant endpoint: the modal offers only what the server
// would accept for this user's CORE role.
function allowedRolesFor(coreRole) {
  if (coreRole === 'admin') return ['hr', 'supervisor', 'hr_manager'];
  if (coreRole === 'auditor') return ['hr', 'supervisor'];
  if (coreRole === 'manager') return ['hr_manager'];
  return [];
}

function roleLabel(role) {
  return ROLE_LABEL[role] || role || '';
}

function describeRole(role, tier) {
  const base = roleLabel(role);
  return tier == null ? base : `${base} (tier ${tier})`;
}

export default function CarePeopleRolePanel({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState(null);

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCarePeopleRole(user.clientUserId);
      setData(res);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [user.clientUserId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function openAssign() {
    setAssignError(null);
    setAssignOpen(true);
  }

  async function handleAssignSubmit({ carePeopleRole, roleScopeTier }) {
    setAssignSubmitting(true);
    setAssignError(null);
    try {
      await grantCarePeopleRole(user.clientUserId, { carePeopleRole, roleScopeTier });
      toast.success('CarePeople role assigned');
      setAssignOpen(false);
      await fetchData();
    } catch (err) {
      // Keep the modal open with an inline error so the admin can correct + retry.
      setAssignError(translateError(err));
    } finally {
      setAssignSubmitting(false);
    }
  }

  async function handleRevokeConfirm() {
    setRevokeSubmitting(true);
    try {
      await revokeCarePeopleRole(user.clientUserId);
      toast.success('CarePeople role revoked');
      setRevokeOpen(false);
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setRevokeSubmitting(false);
    }
  }

  if (loading && !data) {
    return (
      <StatusMessage variant="loading" className="cp-role-panel__state">
        <Loader2 size={16} className="cp-role-panel__spinner" />
        <span>Loading CarePeople role…</span>
      </StatusMessage>
    );
  }

  if (error) {
    return (
      <StatusMessage variant="error" className="cp-role-panel__state cp-role-panel__state--error">
        <AlertCircle size={16} />
        <span>{error}</span>
        <button type="button" className="cp-role-panel__retry" onClick={fetchData}>
          <RotateCw size={14} />
          Retry
        </button>
      </StatusMessage>
    );
  }

  // Defensive: the fetch resolved but returned no body (should not
  // happen — the handler always returns a JSON object). Render nothing
  // rather than crash on a missing field.
  if (!data) return null;

  // carer — no CarePeople module role applies (managers ARE applicable
  // since 2026-07-13: hr_manager restores their module-config write).
  if (!data.coreRoleApplicable) {
    return (
      <section className="cp-role-panel cp-role-panel--na" data-testid="cp-role-panel-na">
        <h3 className="cp-role-panel__heading">CarePeople Module Role</h3>
        <div className="cp-role-panel__na-note">
          <ShieldCheck size={16} />
          <p>
            Module roles are not applicable to carers — carers access only
            their own record.
          </p>
        </div>
      </section>
    );
  }

  const current = data.current;
  const history = data.history || [];
  const allowedRoles = allowedRolesFor(data.coreRole);

  return (
    <section className="cp-role-panel" data-testid="cp-role-panel">
      <h3 className="cp-role-panel__heading">CarePeople Module Role</h3>

      <div className="cp-role-panel__current">
        <div className="cp-role-panel__current-main">
          <span className="cp-role-panel__current-label">Current role</span>
          {current ? (
            <span className="cp-role-panel__current-value" data-testid="cp-role-current">
              <Badge variant="teal">{describeRole(current.carePeopleRole, current.roleScopeTier)}</Badge>
            </span>
          ) : (
            <span className="cp-role-panel__current-value" data-testid="cp-role-current">
              <Badge variant="neutral">None</Badge>
            </span>
          )}
        </div>
        {current && (
          <p className="cp-role-panel__current-meta">
            Assigned {formatDate(current.grantedAt)}{current.grantedBy && <> by {current.grantedBy}</>}
          </p>
        )}
        <div className="cp-role-panel__actions">
          <button type="button" className="cp-role-panel__change" onClick={openAssign}>
            {current ? 'Change role' : 'Assign role'}
          </button>
          {current && (
            <button
              type="button"
              className="cp-role-panel__revoke"
              onClick={() => setRevokeOpen(true)}
            >
              Revoke access
            </button>
          )}
        </div>
      </div>

      <div className="cp-role-panel__history" data-testid="cp-role-history">
        <h4 className="cp-role-panel__history-heading">Role history</h4>
        {history.length === 0 ? (
          <p className="cp-role-panel__history-empty">No role changes recorded yet.</p>
        ) : (
          <ul className="cp-role-panel__history-list">
            {history.map((h, i) => {
              const vals = h.action === 'granted' ? h.newValues : h.oldValues;
              const desc = vals ? describeRole(vals.carePeopleRole, vals.roleScopeTier) : '';
              return (
                <li key={`${h.changedAt}-${i}`} className="cp-role-panel__history-row">
                  <span className="cp-role-panel__history-line">
                    <Badge variant={h.action === 'granted' ? 'success' : 'danger'}>
                      {h.action === 'granted' ? 'Granted' : 'Revoked'}
                    </Badge>
                    <span className="cp-role-panel__history-role">{desc}</span>
                    <span className="cp-role-panel__history-date">{formatDate(h.changedAt)}</span>
                  </span>
                  {h.changedBy && (
                    <span className="cp-role-panel__history-by">by {h.changedBy}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <CarePeopleRoleAssignModal
        isOpen={assignOpen}
        onClose={() => { if (!assignSubmitting) setAssignOpen(false); }}
        onSubmit={handleAssignSubmit}
        submitting={assignSubmitting}
        errorMessage={assignError}
        initialRole={current ? current.carePeopleRole : (allowedRoles[0] || 'hr')}
        initialTier={current ? current.roleScopeTier : 0}
        allowedRoles={allowedRoles}
      />

      <ConfirmDialog
        isOpen={revokeOpen}
        onClose={() => { if (!revokeSubmitting) setRevokeOpen(false); }}
        onConfirm={handleRevokeConfirm}
        title={current ? `Revoke ${roleLabel(current.carePeopleRole)} access?` : 'Revoke access?'}
        message={
          current
            ? `This will remove ${user.email || 'this user'}'s CarePeople access immediately. You can reassign a role later.`
            : ''
        }
        confirmLabel="Confirm revoke"
        isSubmitting={revokeSubmitting}
      />
    </section>
  );
}
